import { useConnection } from "@solana/wallet-adapter-react";
import { useEffect, useState } from "react";
import { getAssociatedTokenAddressSync } from "@solana/spl-token";
import { Transaction } from "@solana/web3.js";
import { useAppSelector } from "@axvdex/state";
import { selectWalletInfo } from "@axvdex/state/wallet/walletSelectors";
import { Bidder, Sale } from "@axvdex/types/solana/outbid";
import Button from "@axvdex/components/common/Button";
import { useGetBidderInfo } from "@axvdex/hooks/solana/useOutbid";
import { findBid, findBidder, findTransferAuthority } from "@axvdex/hooks/solana/utils/DeriveOutbidPDA";
import { createRefundBidsInstruction, errorFromCode } from "@axvdex/solana/outbidAPI/js/generated";
import { sendWithPhantomAndDebugTransaction } from "@axvdex/hooks/solana/utils/client";
import { useUserBalanceContext } from "@axvdex/hooks/solana/useUserBalance";

type RefundButtonProps = {
  selectedSale: Sale;
};
const REFUND_BUTTON_TEXT = "Request Refund";

function RefundButton({ selectedSale }: RefundButtonProps) {
  const walletInfo = useAppSelector(selectWalletInfo);
  const { connection } = useConnection();
  const [isLoading, setIsLoading] = useState(false);
  const [alreadyRefunded, setAlreadyRefunded] = useState(false);
  const [bidder, setBidder] = useState<Bidder | null>(null);
  const [buttonText, setButtonText] = useState(REFUND_BUTTON_TEXT);
  const { triggerFetchUserAssetBalances } = useUserBalanceContext();

  useEffect(() => {
    if (!selectedSale || !walletInfo || !selectedSale.config.isCancelled) {
      return;
    }
    const fetch = async () => {
      setIsLoading(true);
      setButtonText("Loading ...");
      try {
        // Fetch the bidder info
        const bidder = await useGetBidderInfo(connection, selectedSale.saleAccount, window.solanaClient.publicKey);
        setBidder(bidder);

        // Iterate through all bids to check if the current user has already been refunded
        const isAllRefunded = bidder.bids.every(bid => bid.refunded);
        setAlreadyRefunded(isAllRefunded);
      } catch (error) {
        console.error(error);
      } finally {
        setButtonText(REFUND_BUTTON_TEXT);
        setIsLoading(false);
      }
    };
    fetch();
  }, [selectedSale, walletInfo, connection]);

  if (!selectedSale || !walletInfo || !selectedSale.config.isCancelled || !bidder || bidder.bids.length === 0) {
    return null;
  }

  return (
    <Button
      style={{ margin: 4, marginTop: 16, width: "100%" }}
      disabled={isLoading || alreadyRefunded}
      title={alreadyRefunded ? "Already refunded" : "Refund"}
      btnColor="gradientOrange"
      onClick={async () => {
        if (!bidder) {
          return;
        }
        setIsLoading(true);
        setButtonText("Executing ...");
        try {
          const transaction = new Transaction();
          addRefundInstructionToTransaction(selectedSale, bidder, transaction);
          // const signature = await sendWithPhantomAndDebugTransaction(transaction, connection, errorFromCode);
          const signature = await window.solanaClient.sendTransaction(transaction, connection);
          await connection.confirmTransaction(signature, "finalized");
          triggerFetchUserAssetBalances();
          setAlreadyRefunded(true);
        } catch (error) {
          console.error(error);
        } finally {
          setButtonText("Refund");
          setIsLoading(false);
        }
      }}
    >
      {alreadyRefunded ? "Already refunded" : buttonText}
    </Button>
  );
}

export default RefundButton;

// HELPERS ============================================================================================

function addRefundInstructionToTransaction(sale: Sale, bidder: Bidder, transaction: Transaction) {
  const [transferAuthority] = findTransferAuthority(sale.saleAccount);

  // Go through all bids and refund the one that are not already refunded
  for (const bid of bidder.bids) {
    if (bid.refunded) {
      continue;
    }
    // Build the refund instruction
    const [bidderPDA] = findBidder(sale.saleAccount, window.solanaClient.publicKey);
    const [bidPDA] = findBid(sale.saleAccount, bid.id.toNumber());
    const bidderTokenAccount = getAssociatedTokenAddressSync(bid.bidAsset.mint, window.solanaClient.publicKey, true);
    const saleTokenAccount = getAssociatedTokenAddressSync(bid.bidAsset.mint, transferAuthority, true);
    const refundTx = createRefundBidsInstruction({
      saleAccount: sale.saleAccount,
      transferAuthority: transferAuthority,
      bidder: bidderPDA,
      bidderAuthority: window.solanaClient.publicKey,
      bid: bidPDA,
      bidAssetDest: bidderTokenAccount,
      bidAssetSrc: saleTokenAccount,
      bidAssetMint: bid.bidAsset.mint,
    });
    console.log("Refund Instruction: ", refundTx, " for bid: ", bid.id.toNumber());

    // Add the refund instruction to the transaction
    transaction.add(refundTx);
  }
}
